/**
 * Auth Store — 認證狀態管理
 *
 * 使用 Zustand 管理登入/註冊/Token 狀態。
 * 啟動時自動從 localStorage 恢復 Token。
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  register as apiRegister,
  login as apiLogin,
  getMe,
  loadAccessToken,
  loadRefreshToken,
  clearTokens,
  type PlayerInfo,
  type ApiError,
} from '../services/NetworkManager';

interface AuthStore {
  // 狀態
  isLoggedIn: boolean;
  player: PlayerInfo | null;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  error: string | null;

  // 動作
  register: (username: string, password: string) => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loadFromStorage: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => ({
      isLoggedIn: false,
      player: null,
      token: null,
      refreshToken: null,
      isLoading: false,
      error: null,

      register: async (username: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const resp = await apiRegister(username, password);
          const playerInfo: PlayerInfo = {
            id: resp.player.id,
            username: resp.player.username,
            mmr: resp.player.mmr,
            level: resp.player.level,
            wins: resp.player.wins,
            losses: resp.player.losses,
          };
          set({
            isLoggedIn: true,
            player: playerInfo,
            token: resp.tokens.access_token,
            refreshToken: resp.tokens.refresh_token,
            isLoading: false,
            error: null,
          });
        } catch (err) {
          const apiErr = err as ApiError;
          set({
            isLoading: false,
            error: apiErr.message || '註冊失敗',
          });
        }
      },

      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const resp = await apiLogin(username, password);
          const playerInfo: PlayerInfo = {
            id: resp.player.id,
            username: resp.player.username,
            mmr: resp.player.mmr,
            level: resp.player.level,
            wins: resp.player.wins,
            losses: resp.player.losses,
          };
          set({
            isLoggedIn: true,
            player: playerInfo,
            token: resp.tokens.access_token,
            refreshToken: resp.tokens.refresh_token,
            isLoading: false,
            error: null,
          });
        } catch (err) {
          const apiErr = err as ApiError;
          set({
            isLoading: false,
            error: apiErr.message || '登入失敗',
          });
        }
      },

      logout: () => {
        clearTokens();
        set({
          isLoggedIn: false,
          player: null,
          token: null,
          refreshToken: null,
          error: null,
        });
      },

      loadFromStorage: async () => {
        const token = loadAccessToken();
        const refToken = loadRefreshToken();

        if (!token) {
          return;
        }

        set({ isLoading: true });
        try {
          const player = await getMe(token);
          set({
            isLoggedIn: true,
            player,
            token,
            refreshToken: refToken,
            isLoading: false,
          });
        } catch {
          // Token 過期或無效，清除
          clearTokens();
          set({
            isLoggedIn: false,
            player: null,
            token: null,
            refreshToken: null,
            isLoading: false,
          });
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    { name: 'AuthStore' },
  ),
);
